import React from "react";
import { Machine, assign } from "xstate";
import { useMachine } from "@xstate/react";
import Loading from "../Loading";
import axios from "axios";

function appendBlobOrText(formData, dataItem, name) {
  dataItem.hasOwnProperty("name")
    ? formData.append(name, dataItem, dataItem.name)
    : formData.append(name, dataItem);
}

/**
 * Converts string into float
 *
 * @param val string
 * @return float
 */
function stringToFloat(val) {
  if (typeof val === "undefined") return val;
  return Number(val.replace(/[^0-9.-]+/g, ""));
}

function mapDataToForm(data) {
  // flatten the wizard information (minus images)
  const formFields = {
    firstName: data.step1.firstName,
    lastName: data.step1.lastName,
    title: data.step2.title,
    email: data.step1.email,
    phoneNumber: data.step1.phoneNumber,
    companyName: data.step2.companyName,
    streetAddress: data.step2.streetAddress,
    streetAddress2: data.step2.streetAddress2 || "",
    city: data.step2.city,
    state: data.step2.state,
    postalCode: data.step2.postalCode,
    companyPhoneNumber: data.step2.companyPhoneNumber,
    equipmentType: data.step3.equipmentType,
    manufacturer: data.step3.manufacturer,
    make: data.step3.make,
    model: data.step3.model,
    year: data.step3.year,
    identifierType: data.step3.identifier,
    serialNumber: data.step3.serialNumber || "",
    vin: data.step3.vin || "",
    quantity: data.step3.quantity,
    hoursMiles: data.step3.hoursMiles,
    hoursMilesType: data.step3.hoursMilesType,
    productDescription: data.step4.description,
    productLocationDifferent: data.step4.locationDifferent,
    productStreetAddress: data.step4.streetAddress,
    productStreetAddress2: data.step4.streetAddress2,
    productState: data.step4.state,
    productCity: data.step4.city,
    productPostalCode: data.step4.postalCode,
    hasLien: data.step4.hasLien,
    lienHolderName: data.step4.lienHolderName,
    lienHolderContact: data.step4.lienHolderContact,
    lienPayoutAmount: data.step4.lienPayoutAmount,
    requiresDismantling: data.step4.requiresDismantling,
    dismantleDays: data.step4.dismantleDays,
    willHelpDismantle: data.step4.willHelpDismantle,
    dismantleHelpDescription: data.step4.dismantleHelpDescription,
    providesDisconnect: data.step4.providesDisconnect,
    requiresCrane: data.step4.requiresCrane,
    numberOfTrucks: data.step4.numberOfTrucks,
    requiresLoading: data.step4.requiresLoading,
    willLoadEquipment: data.step4.willLoadEquipment,
    hasLoadingEquipment: data.step4.hasLoadingEquipment,
    loadingEquipmentDescription: data.step4.loadingEquipmentDescription,
    listingType: data.step5.listingType,
    buyNowPrice: stringToFloat(data.step5.buyNowPrice),
    reservePrice: stringToFloat(data.step5.reservePrice),
  };

  let form = new FormData();

  // add form fields for the flattened object
  Object.keys(formFields).forEach((fieldKey) => {
    form.append(fieldKey, formFields[fieldKey]);
  });

  // add form fields for the images
  let images = data.step3.images || [];
  images.forEach((image, index) => {
    appendBlobOrText(form, image.value, image.name);
  });

  return form;
}

async function postData(data) {
  const endpoint = process.env.REACT_APP_API_ENDPOINT;
  const fields = mapDataToForm(data);

  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
  };

  return await axios.post(`${endpoint}/createListing`, fields, config);
}

function getProductSubmitMachine(data) {
  const config = {
    id: "styleConfig",
    initial: "loading",
    context: {
      result: null,
      error: null,
    },
    states: {
      idle: {
        on: { FETCH: "loading" },
      },
      loading: {
        invoke: {
          src: async () => postData(data),
          onDone: {
            target: "success",
            actions: assign({
              result: (_, event) => (event as any).data,
            }),
          },
          onError: {
            target: "failure",
            actions: assign({
              error: (_, event) => (event as any).data,
            }),
          },
        },
        cancel: { CANCEL: "idle" },
      },
      success: { on: { FETCH: "loading" } },
      failure: { on: { FETCH: "loading" } },
    },
  };

  return Machine(config as any);
}

export function useProductSubmit(data) {
  const [current, send] = useMachine(getProductSubmitMachine(data));
  const { error, result } = current.context;

  const submit = () => {
    send("FETCH");
  };

  const cancel = () => {
    send("CANCEL");
  };

  const isLoading = current.matches("loading");
  const isErrored = current.matches("failure");

  return { submit, cancel, isLoading, result, error, isErrored };
}

export default function ({
  data,
  shouldSkipProcess,
}: {
  data: any;
  shouldSkipProcess?: boolean;
}) {
  const success = (
    <>
      <h1>Thank you!</h1>
      <p>Your equipment listing request has been received.</p>
      <p>
        You will receive an email from DocuSign shortly with your listing
        agreement.
      </p>
      <p>Please read it over and sign and initial where necessary.</p>
      <p>
        Once that has been signed we will start working on posting your
        equipment!
      </p>
      <p>
        If we have any questions or need more information on your equipment we
        will be in touch.
      </p>
      <p>
        When your equipment goes live you’ll get an email from us with further
        details. Please check your spam folder if you don’t see anything.
      </p>
    </>
  );

  if (shouldSkipProcess) {
    return success;
  }

  const {
    submit,
    cancel,
    isLoading,
    result,
    error,
    isErrored,
  } = useProductSubmit(data);

  if (isLoading) {
    return <Loading />;
  }

  if (isErrored) {
    return (
      <>
        <h1>An Error has occurred</h1>
        <p>Please contact support</p>
      </>
    );
  }

  return success;
}
